import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ApiError, downloadCompatPdf, fetchCompat, fetchCompatAi, fetchTypePair, requestCompatAi, requestCompatScenes } from '../api/client';
import type { CompatAiStatus, CompatItem, CompatRead, CompatResponse, CompatScenes, CompatSideBrief, DiagnosisResult, TypePairGuide } from '../api/types';
import { useAuth } from '../auth/AuthProvider';
import { jungianType } from '../diagnosis/labels';
import { Portrait } from './Portrait';

/**
 * 相性と付き合い方（doc/ASSESSMENT.md §10）。結果ページの一節。
 * - 詳細版（要ログイン）: 相手の結果 URL を貼ると、2 人の側面スコアを並べて、はっきり違うところ・同じ側に寄るところと手当てを出す。
 *   比べた状態は `?with={resultId}`（選んだ関係は `&rel=`）に持つので、その URL をそのまま共有できる（開く人もログインが要る）。
 *   相手との関係（恋人・家族・上司など）を選ぶと、AI（Gemini 4 Argon）が関係に合わせて読み直す。1 人 1 日の回数あり、読み済みは数えない。
 *   読みの 3 つの場面は、頼むと 2 人のキャラのエピソードの絵になる（1 回 3 枚。読みとは別の回数。描き済みは数えない）。
 *   この詳細版だけ PDF にできる（`GET /v1/compat/pdf`。トークンを添えて取る）。
 * - 型どうしの一般論は、ドロップダウンで相手の型を選ぶ（決め打ちの文。packages/character/src/compat.ts）。
 *   型だけで決め打ちした一般論なので PDF には載せず、画面にもそう書く。
 * 「相性 ○%」のような点数は出さない。
 */

const TYPES16 = ['E', 'I'].flatMap((e) => ['N', 'S'].flatMap((n) => ['F', 'T'].flatMap((f) => ['J', 'P'].map((j) => `${e}${n}${f}${j}`))));

/** 貼られた文字列から結果 ID を取り出す。`https://…/r/{id}`・`/r/{id}?with=…`・ID だけ、のどれでも受ける。 */
export function resultIdFrom(input: string): string | null {
  const text = input.trim();
  const fromUrl = /\/r\/([A-Za-z0-9_-]{1,64})(?:[/?#]|$)/.exec(text);
  if (fromUrl) return fromUrl[1];
  return /^[A-Za-z0-9_-]{1,64}$/.test(text) ? text : null;
}

export function CompatSection({ result, shareUrl }: { result: DiagnosisResult; shareUrl: string }) {
  const auth = useAuth();
  const signedIn = Boolean(auth.user);
  const [params, setParams] = useSearchParams();
  const partnerId = params.get('with');
  const [input, setInput] = useState('');
  const [inputError, setInputError] = useState('');
  const [copied, setCopied] = useState(false);
  // 「比べる」を押したか。比べた結果はフォームの下に出るので、そのままだと出たことに気づきにくい
  const revealReport = useRef(false);

  // 共有された「比べた URL」から来たら、この節まで送る
  useEffect(() => {
    if (partnerId) document.getElementById('compat')?.scrollIntoView({ block: 'start' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 「比べる」を押したら、比べた結果（読み込み中の表示から）まで送る
  useEffect(() => {
    if (revealReport.current && scrollToReport()) revealReport.current = false;
  }, [partnerId, signedIn]);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!signedIn && !(await auth.requestSignIn())) return;
    const id = resultIdFrom(input);
    if (!id) {
      setInputError('結果ページの URL（…/r/ で始まるもの）を貼ってください。');
      return;
    }
    if (id === result.resultId) {
      setInputError('これはこの結果の URL です。相手の結果の URL を貼ってください。');
      return;
    }
    setInputError('');
    // 同じ相手ならもう出ているので、そこまで送るだけ
    if (id === partnerId && scrollToReport()) return;
    revealReport.current = true;
    setParams((prev) => { const next = new URLSearchParams(prev); next.set('with', id); return next; }, { replace: false });
  };

  const copy = () => {
    void navigator.clipboard?.writeText(shareUrl).then(() => { setCopied(true); window.setTimeout(() => setCopied(false), 2000); }).catch(() => undefined);
  };

  return (
    <section className="section compat" id="compat">
      <h2 className="section__title">相性と付き合い方</h2>
      <p className="section__body">
        相手との性格の違いから、どこですれ違いやすく、どう付き合えばよいかを読みます。相性の良し悪しを点数で決めるものではありません。
      </p>

      <div className="panel compat__panel">
        <h3 className="panel__title">相手の結果と比べる（詳細版・ログインが必要です）</h3>
        <label className="field">
          <span className="field__label">あなたの結果の URL（相手に送ると、相手の結果ページからも比べられます）</span>
          <span className="compat__row">
            <input className="field__input" type="text" readOnly value={shareUrl} onFocus={(event) => event.currentTarget.select()} />
            <button className="button button--ghost" type="button" onClick={copy}>{copied ? 'コピーしました' : 'コピー'}</button>
          </span>
        </label>
        <form onSubmit={(event) => void submit(event)}>
          <label className="field">
            <span className="field__label">相手の結果の URL</span>
            <span className="compat__row">
              <input
                className="field__input"
                type="text"
                inputMode="url"
                placeholder="https://…/r/…"
                value={input}
                onChange={(event) => setInput(event.target.value)}
              />
              <button className="button button--primary" type="submit" disabled={!input.trim()}>{signedIn ? '比べる' : 'ログインして比べる'}</button>
            </span>
          </label>
          {inputError && <p className="maker__error" role="alert">{inputError}</p>}
        </form>
        <p className="section__note">
          詳細版では、相手との関係（恋人・家族・上司・部下など）を選ぶと、AI が関係に合わせた付き合い方も書きます（1 日の回数に上限があります）。
          別の診断の結果とも比べられます（設問は全診断で共通です）。結果ページは URL を知っている人なら誰でも開けるページなので、比べる前に相手に一声かけましょう。
        </p>
      </div>

      {partnerId && signedIn && <CompatReport youId={result.resultId} partnerId={partnerId} />}
      {partnerId && auth.ready && !signedIn && (
        <div className="panel compat__panel">
          <p className="section__body">比べた結果（詳細版）を見るには、ログインが必要です。</p>
          <div className="page__actions">
            <button className="button button--primary" type="button" onClick={() => void auth.requestSignIn()}>ログインして見る</button>
          </div>
        </div>
      )}

      <TypePairPicker type16={result.type16} />
    </section>
  );
}

/** 比べた結果まで送る。まだ出ていなければ false。 */
function scrollToReport(): boolean {
  const report = document.getElementById('compat-report');
  if (!report) return false;
  const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
  report.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
  return true;
}

function CompatReport({ youId, partnerId }: { youId: string; partnerId: string }) {
  const [params, setParams] = useSearchParams();
  const relation = params.get('rel') ?? '';
  const setRelation = (rel: string) =>
    setParams((prev) => { const next = new URLSearchParams(prev); if (rel) next.set('rel', rel); else next.delete('rel'); return next; }, { replace: true });
  const [data, setData] = useState<CompatResponse | null>(null);
  const [error, setError] = useState('');
  const [aiRead, setAiRead] = useState<CompatRead | null>(null);
  const [pdfState, setPdfState] = useState<'idle' | 'busy' | string>('idle');

  useEffect(() => {
    let cancelled = false;
    setData(null);
    setError('');
    void fetchCompat(youId, partnerId)
      .then((value) => { if (!cancelled) setData(value); })
      .catch((cause: unknown) => {
        if (!cancelled) setError(cause instanceof ApiError ? cause.message : '比べられませんでした。時間をおいてもう一度お試しください。');
      });
    return () => { cancelled = true; };
  }, [youId, partnerId]);

  // 読み込み中・失敗も同じ入れ物（#compat-report）に出す。「比べる」を押したらここまで送る
  if (error) return <div className="compat__report" id="compat-report"><p className="maker__error" role="alert">相手の結果: {error}</p></div>;
  if (!data) return <div className="compat__report" id="compat-report"><p className="section__note" role="status">比べています…</p></div>;

  const { you, partner, compat } = data;
  const shown = new Set([...compat.focus, ...compat.common].map((x) => x.facet));
  const fewer = compat.measured < 18;
  const normMismatch = (you.normVersion > 0) !== (partner.normVersion > 0);

  return (
    <div className="compat__report" id="compat-report">
      <div className="compat__pair">
        <SideCard side={you} who="あなた" />
        <span className="compat__times" aria-hidden="true">×</span>
        <SideCard side={partner} who="相手" />
      </div>

      <p className="section__body">{compat.summary}</p>
      <AiPanel youId={you.resultId} partnerId={partner.resultId} relation={relation} onRelation={setRelation} onRead={setAiRead} />

      <p className="compat__legend">
        <span className="compat__dot compat__dot--you" />あなた　<span className="compat__dot compat__dot--partner" />相手
      </p>

      {compat.focus.length > 0 && (
        <>
          <h3 className="compat__heading">まず話し合っておきたいこと</h3>
          <ol className="compat__items">{compat.focus.map((item) => <CompatCard key={item.facet} item={item} />)}</ol>
        </>
      )}

      {compat.common.length > 0 && (
        <>
          <h3 className="compat__heading">噛み合うところと、重なる死角</h3>
          <ol className="compat__items">{compat.common.map((item) => <CompatCard key={item.facet} item={item} />)}</ol>
        </>
      )}

      <details className="facets">
        <summary>場面ごとに、{compat.measured} の側面を並べて見る</summary>
        {compat.areas.map((area) => {
          const rest = area.items.filter((x) => !shown.has(x.facet));
          return (
            <div key={area.axis} className="compat__area">
              <h4 className="compat__area-title">{area.area}<span className="compat__area-caption">（{area.caption}）</span></h4>
              <ul className="facets__list">
                {area.facets.map((f) => (
                  <li key={f.facet} className="compat__facet">
                    <span className="facets__name">{f.label}</span>
                    <DualBar you={f.you} partner={f.partner} />
                    <span className="facets__value">{f.you} / {f.partner}</span>
                  </li>
                ))}
              </ul>
              {rest.length > 0 && <ul className="compat__items compat__items--plain">{rest.map((item) => <CompatCard key={item.facet} item={item} />)}</ul>}
            </div>
          );
        })}
      </details>

      <details className="method">
        <summary>この比べ方について</summary>
        <ul className="method__list">
          <li>
            2 人とも測った側面ごとに、スコアの差が {compat.thresholds.gap} 以上なら「はっきり違う」としています。1 つの側面は 3〜4 問で測るので誤差があり、
            これより小さい差は測定の誤差で説明がつくためです。2 人とも中央（50）から {compat.thresholds.lean} 以上同じ側に離れていれば「二人とも同じ側」としています。
          </li>
          {fewer && <li>どちらかが短い版で受けているため、比べられたのは {compat.measured} の側面だけです。2 人とも「じっくり」で受けると 18 の側面すべてを比べられます。</li>}
          {data.mapped && <li>ビッグファイブ版の結果は、5 因子から 6 軸 18 側面に写した目安のスコアで比べています。</li>}
          {normMismatch && <li>一方は素点、もう一方は受けた人の中での位置（百分位）でスコアを出しているため、差は目安です。</li>}
          <li>研究では、性格が似ているかどうかで関係の満足度が説明できる割合はごく小さいと報告されています。違いは問題ではなく、手当ての仕方を知っておくための手がかりです。</li>
          <li>結果は自己申告にもとづく傾向の見立てで、関係の行方を判定するものではありません。</li>
        </ul>
      </details>

      <div className="page__actions">
        <button
          className="button button--primary"
          type="button"
          disabled={pdfState === 'busy'}
          onClick={() => {
            setPdfState('busy');
            void downloadCompatPdf(you.resultId, partner.resultId, aiRead ? relation : null)
              .then(() => setPdfState('idle'))
              .catch((cause: unknown) => setPdfState(cause instanceof ApiError ? cause.message : 'PDF を作れませんでした。'));
          }}
        >
          {pdfState === 'busy' ? 'PDF を作っています…' : 'この比較を PDF でダウンロード'}
        </button>
      </div>
      {pdfState !== 'idle' && pdfState !== 'busy' && <p className="maker__error" role="alert">{pdfState}</p>}
      <p className="section__note">
        PDF には 2 人のキャラ・まとめ・側面ごとの比較と手当て・比べ方の説明と、この比較の URL が載ります（氏名などは載りません）。
        {aiRead ? 'いま表示している関係の AI の読み（描き終わったエピソードの絵も）も載ります。' : 'AI の読みは、読んだあとにダウンロードすると載ります。'}
        下の「型どうしの付き合い方（一般論）」は載りません。
      </p>
      <p className="section__note">
        <Link to={`/r/${encodeURIComponent(partner.resultId)}?with=${encodeURIComponent(you.resultId)}#compat`}>相手の立場から読む</Link>
        　この比べた結果は、いまのページの URL で共有できます。
      </p>
    </div>
  );
}

/** 描いている途中の絵を見に行く間隔。 */
const SCENE_POLL_MS = 4000;
const sceneSrc = (path: string) => `/${path.replace(/^\/+/, '')}`;

/**
 * 関係に合わせた AI の読み。関係を選ぶと、読み済みならすぐ出る（数えない）。まだなら「AI で読む」で頼む（1 日の回数を使う）。
 * 読みがあれば「エピソードの絵を描く」で 3 つの場面を絵にできる（裏で描くので、描き終わるまで見に行く）。
 */
function AiPanel({ youId, partnerId, relation, onRelation, onRead }: {
  youId: string; partnerId: string; relation: string; onRelation: (rel: string) => void; onRead: (read: CompatRead | null) => void;
}) {
  const [status, setStatus] = useState<CompatAiStatus | null>(null);
  const [read, setRead] = useState<CompatRead | null>(null);
  const [scenes, setScenes] = useState<CompatScenes | null>(null);
  const [busy, setBusy] = useState(false);
  const [sceneBusy, setSceneBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    setError('');
    setScenes(null);
    void fetchCompatAi(youId, partnerId, relation)
      .then((value) => { if (cancelled) return; setStatus(value); setRead(value.read); setScenes(value.scenes); onRead(value.read); })
      .catch((cause: unknown) => { if (!cancelled) setError(cause instanceof ApiError ? cause.message : '読み込めませんでした。'); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [youId, partnerId, relation]);

  // 描いている途中なら、描き終わるまで見に行く（数えない GET）
  const drawing = scenes?.status === 'running';
  useEffect(() => {
    if (!drawing || !relation) return undefined;
    let cancelled = false;
    const timer = window.setInterval(() => {
      void fetchCompatAi(youId, partnerId, relation)
        .then((value) => { if (!cancelled) { setScenes(value.scenes); setStatus(value); } })
        .catch(() => undefined);
    }, SCENE_POLL_MS);
    return () => { cancelled = true; window.clearInterval(timer); };
  }, [drawing, youId, partnerId, relation]);

  const draw = () => {
    setSceneBusy(true);
    setError('');
    void requestCompatScenes(youId, partnerId, relation)
      .then((value) => {
        setScenes(value.scenes);
        setStatus((prev) => (prev ? { ...prev, scenes: value.scenes, sceneQuota: value.sceneQuota } : prev));
      })
      .catch((cause: unknown) => setError(cause instanceof ApiError ? cause.message : 'エピソードの絵を頼めませんでした。時間をおいてもう一度お試しください。'))
      .finally(() => setSceneBusy(false));
  };

  const ask = () => {
    setBusy(true);
    setError('');
    void requestCompatAi(youId, partnerId, relation)
      .then((value) => {
        setRead(value.read);
        setScenes(value.scenes);
        onRead(value.read);
        setStatus((prev) => (prev ? { ...prev, read: value.read, relation: value.relation, quota: value.quota } : prev));
      })
      .catch((cause: unknown) => setError(cause instanceof ApiError ? cause.message : 'AI の読みを作れませんでした。時間をおいてもう一度お試しください。'))
      .finally(() => setBusy(false));
  };

  const quota = status?.quota;
  const sceneQuota = status?.sceneQuota;
  const relations = status?.relations ?? {};
  const canDraw = Boolean(status?.scenesAvailable) && (!scenes || scenes.status === 'partial');
  return (
    <div className="panel compat__panel compat__ai">
      <h3 className="panel__title">関係に合わせて読む（AI）</h3>
      <label className="field">
        <span className="field__label">相手との関係</span>
        <select className="field__input" value={relation} onChange={(event) => onRelation(event.target.value)}>
          <option value="">関係を選ぶ</option>
          {Object.entries(relations).map(([key, label]) => <option key={key} value={key}>{label}</option>)}
        </select>
      </label>
      {relation && !read && (
        <div className="page__actions">
          <button className="button button--primary" type="button" onClick={ask} disabled={busy || quota?.remaining === 0}>
            {busy ? 'AI が読んでいます…' : 'AI で読む'}
          </button>
        </div>
      )}
      {quota && (
        <p className="section__note">
          {quota.limit === null ? 'AI の読みは回数の制限なし（管理者）。' : `AI の読みは 1 日 ${quota.limit} 回まで、今日はあと ${quota.remaining} 回。`}
          読み済みの組み合わせを開くのは数えません。
        </p>
      )}
      {error && <p className="maker__error" role="alert">{error}</p>}
      {read && (
        <div className="compat__ai-read">
          <p className="section__body">{read.overview}</p>
          <dl className="compat__dl">
            {read.points.map((p, i) => {
              const image = scenes?.images[i] ?? null;
              return (
                <div key={p.title}>
                  <dt>{p.title}</dt>
                  <dd>
                    {p.body}
                    {image && (
                      <figure className="compat__scene">
                        <img src={sceneSrc(image)} alt={`「${p.title}」の場面のイラスト`} width={768} height={576} loading="lazy" />
                      </figure>
                    )}
                    {!image && drawing && i < (scenes?.images.length ?? 0) && (
                      <span className="compat__scene compat__scene--pending" role="status">絵を描いています…</span>
                    )}
                  </dd>
                </div>
              );
            })}
            {read.avoid.length > 0 && (
              <>
                <dt>避けたいこと</dt>
                <dd><ul className="compat__tips">{read.avoid.map((x) => <li key={x}>{x}</li>)}</ul></dd>
              </>
            )}
          </dl>
          {canDraw && (
            <div className="page__actions">
              <button className="button" type="button" onClick={draw} disabled={sceneBusy || sceneQuota?.remaining === 0}>
                {sceneBusy ? '頼んでいます…' : scenes?.status === 'partial' ? '描けなかった絵を描き直す' : 'エピソードの絵を描く（3 枚）'}
              </button>
            </div>
          )}
          {canDraw && sceneQuota && (
            <p className="section__note">
              {sceneQuota.limit === null ? 'エピソードの絵は回数の制限なし（管理者）。' : `エピソードの絵は 1 日 ${sceneQuota.limit} 回まで（1 回で 3 枚）、今日はあと ${sceneQuota.remaining} 回。`}
              描き終わった組み合わせを開くのは数えません。
            </p>
          )}
          <p className="section__note">
            この読みは AI（Google Gemini）が下の比較をもとに書いた文章です。誤りや思い込みを含むことがあります。
            {scenes && 'エピソードの絵も AI が描いた想像の場面です。'}
          </p>
        </div>
      )}
    </div>
  );
}

function SideCard({ side, who }: { side: CompatSideBrief; who: string }) {
  return (
    <div className="compat__side">
      {side.character && <Portrait brief={side.character} size={72} className="mini__portrait" loading="lazy" />}
      <div>
        <p className="mini__label">{who}</p>
        {side.character ? (
          <p className="mini__name"><Link to={side.href}>{side.character.name}</Link></p>
        ) : (
          <p className="mini__name"><Link to={side.href}>結果を見る</Link></p>
        )}
        <p className="compat__meta">{side.theme.title}・<span className="typecode">{side.displayType}</span></p>
      </div>
    </div>
  );
}

function DualBar({ you, partner }: { you: number; partner: number }) {
  const at = (n: number) => `${Math.min(97, Math.max(3, n))}%`;
  const lo = Math.min(you, partner);
  const hi = Math.max(you, partner);
  return (
    <span className="compat__bar">
      <span className="compat__span" style={{ left: `${lo}%`, width: `${hi - lo}%` }} />
      <span className="compat__marker compat__marker--partner" style={{ left: at(partner) }} />
      <span className="compat__marker compat__marker--you" style={{ left: at(you) }} />
    </span>
  );
}

function CompatCard({ item }: { item: CompatItem }) {
  return (
    <li className={`compat__item compat__item--${item.kind}`}>
      <p className="compat__item-head">
        <strong>{item.label}</strong>
        <span className="compat__badge">{item.kind === 'gap' ? `差 ${Math.abs(item.diff)}` : '二人とも同じ側'}</span>
      </p>
      <DualBar you={item.you} partner={item.partner} />
      {item.kind === 'gap' ? (
        <p className="compat__traits">あなた（{item.you}）: {item.youTrait}／相手（{item.partner}）: {item.partnerTrait}</p>
      ) : (
        <p className="compat__traits">二人とも: {item.youTrait}（{item.you}・{item.partner}）</p>
      )}
      <p className="compat__advice">{item.advice}</p>
    </li>
  );
}

/** 型どうしの一般論。相手の型をドロップダウンで選ぶ。 */
function TypePairPicker({ type16 }: { type16: string }) {
  const [partner, setPartner] = useState('');
  const [guide, setGuide] = useState<TypePairGuide | null>(null);
  const [error, setError] = useState('');
  const valid = /^[EI][SN][TF][JP]$/.test(type16);

  useEffect(() => {
    if (!partner || !valid) return;
    let cancelled = false;
    // 前の型の読みは、次が届くまで出したままにする（いったん消すと節が縮み、読んでいた位置がずれる）
    setError('');
    void fetchTypePair(type16, partner)
      .then((value) => { if (!cancelled) setGuide(value.guide); })
      .catch(() => { if (!cancelled) { setGuide(null); setError('読み込めませんでした。時間をおいてもう一度お試しください。'); } });
    return () => { cancelled = true; };
  }, [type16, partner, valid]);

  if (!valid) return null;
  const label = (code: string) => {
    const jung = jungianType(code);
    return jung ? `${code}（主機能 ${jung.dominant}）` : code;
  };

  return (
    <div className="panel compat__panel">
      <h3 className="panel__title">型どうしの付き合い方（一般論）<span className="compat__nopdf">PDF には載りません</span></h3>
      <p className="section__note compat__nopdf-note">
        型の 4 文字だけから決め打ちで組んだ一般論なので、画面で読む目安としてだけ出しています。PDF にしたいときは、上で相手の結果 URL と比べた詳細版をダウンロードしてください。
      </p>
      <label className="field">
        <span className="field__label">
          あなたの型 <strong className="typecode">{type16}</strong> と、相手の型
        </span>
        <select className="field__input" value={partner} onChange={(event) => setPartner(event.target.value)}>
          <option value="">相手の型を選ぶ</option>
          {TYPES16.map((code) => <option key={code} value={code}>{label(code)}</option>)}
        </select>
      </label>
      {error && <p className="maker__error" role="alert">{error}</p>}
      {partner && !guide && !error && <p className="section__note">読み込んでいます…</p>}
      {partner && guide && (
        <div className="compat__guide">
          <p className="section__body">{guide.summary}</p>
          {guide.axes.map((axis) => (
            <div key={axis.axis} className={`compat__axis compat__axis--${axis.relation}`}>
              <h4 className="compat__area-title">
                {axis.area}<span className="compat__area-caption">（{axis.caption}）</span>
                <span className="compat__badge">
                  あなた {axis.you.name}（{axis.you.code}）× 相手 {axis.partner.name}（{axis.partner.code}）・{axis.relation === 'same' ? '同じ' : '違う'}
                </span>
              </h4>
              <dl className="compat__dl">
                <dt>噛み合うところ</dt>
                <dd>{axis.works}</dd>
                <dt>すれ違いやすいところ</dt>
                <dd>{axis.friction}</dd>
                <dt>あなたにできること</dt>
                <dd><ul className="compat__tips">{axis.tips.map((tip) => <li key={tip}>{tip}</li>)}</ul></dd>
              </dl>
            </div>
          ))}
          <p className="section__note">{guide.note}</p>
        </div>
      )}
    </div>
  );
}
